import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { PROVINCES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "ciftora_nearby_province";

const SCOPE_LABEL = {
  province: "ilinizdeki ilanlar",
  region: "bölgenizdeki ilanlar",
  national: "Türkiye genelindeki ilanlar",
};

export default function NearbyListingsStrip({ onFavorite }) {
  const { user } = useAuth();
  const [province, setProvince] = useState(
    () => localStorage.getItem(STORAGE_KEY) || user?.province || ""
  );
  const [data, setData] = useState(null);
  const railRef = useRef(null);

  useEffect(() => {
    if (!province && user?.province) setProvince(user.province);
  }, [user?.province]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    api
      .get("/listings/nearby/strip", { params: province ? { province, limit: 12 } : { limit: 12 } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [] }));
    return () => {
      alive = false;
    };
  }, [province]);

  const items = data?.items || [];
  const label = useMemo(() => SCOPE_LABEL[data?.scope] || SCOPE_LABEL.national, [data?.scope]);

  const scroll = (dir) => {
    const rail = railRef.current;
    if (rail) rail.scrollBy({ left: dir * rail.clientWidth * 0.85, behavior: "smooth" });
  };

  const pick = (value) => {
    setProvince(value);
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  };

  if (data && !items.length) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 pt-14 lg:px-8" data-testid="nearby-strip">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">
            Size En Yakın İlanlar
          </h2>
          <p className="mt-2 flex items-center gap-1.5 text-base text-muted-foreground" data-testid="nearby-strip-scope">
            <MapPin className="h-4 w-4" />
            {province ? `${province} ve çevresi — ${label}` : `İl seçin veya ${label}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={province}
            onChange={(e) => pick(e.target.value)}
            data-testid="nearby-strip-province"
            className="h-11 rounded-lg border border-border bg-background px-3 text-sm font-semibold text-primary"
          >
            <option value="">Tüm Türkiye</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => scroll(-1)}
            data-testid="nearby-strip-prev"
            aria-label="Geri kaydır"
            className="hidden h-11 w-11 items-center justify-center rounded-full border border-border text-primary transition-colors duration-200 hover:border-secondary md:inline-flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            data-testid="nearby-strip-next"
            aria-label="İleri kaydır"
            className="hidden h-11 w-11 items-center justify-center rounded-full border border-border text-primary transition-colors duration-200 hover:border-secondary md:inline-flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        ref={railRef}
        className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((l) => (
          <div key={l.id} className="w-[78%] shrink-0 snap-start sm:w-[46%] lg:w-[31%]">
            <ListingCard listing={l} onFavorite={onFavorite} />
          </div>
        ))}
        {!data &&
          [0, 1, 2].map((i) => (
            <div key={i} className="h-72 w-[78%] shrink-0 animate-pulse rounded-lg bg-muted sm:w-[46%] lg:w-[31%]" />
          ))}
      </div>

      <Link
        to={province ? `/ilanlar?province=${encodeURIComponent(province)}` : "/ilanlar"}
        data-testid="nearby-strip-all"
        className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-secondary hover:underline"
      >
        {province ? `${province} ilanlarının tümü` : "Tüm ilanlar"} <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}
